import { FC, useEffect } from 'react';
import { CalendarComponentProps } from './calendarComponent.interfaces';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { EventClickArg, EventContentArg, EventInput, DatesSetArg } from '@fullcalendar/core';
import { useTheme, Box } from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useResponsive } from 'src/hooks/useResponsiveHook';

const CalendarComponent: FC<CalendarComponentProps> = ({
  events,
  onDateClick,
  onEventClick,
  onMonthChange,
}) => {
  const theme = useTheme();
  const { translateText, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  let eventGuid = 0;
  let todayStr = new Date().toISOString().replace(/T.*$/, '');

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .fc {
        font-family: ${theme.typography.fontFamily};
        color: ${theme.palette.text.primary};
      }
      .fc-header-toolbar {
        margin-bottom: ${isMobile ? '1em' : '1.5em'};
        flex-direction: ${isMobile ? 'column' : 'row'};
        gap: ${isMobile ? '0.5em' : '0'};
      }
      .fc-toolbar-chunk {
        display: flex;
        flex-wrap: wrap;
        gap: ${isMobile ? '0.25em' : '0.5em'};
        justify-content: ${isMobile ? 'center' : 'flex-start'};
      }
      .fc-button {
        background-color: ${theme.palette.background.paper} !important;
        border-color: ${theme.palette.divider} !important;
        color: ${theme.palette.text.primary} !important;
        border-radius: 999px;
        font-weight: 600;
        text-transform: none;
        padding: ${isMobile ? '10px 12px' : '8px 16px'};
        min-height: ${isMobile ? '44px' : '36px'};
        font-size: ${isMobile ? '0.875rem' : '0.875rem'};
      }
      .fc-button:hover {
        background-color: ${theme.palette.primary.light} !important;
        border-color: ${theme.palette.primary.main} !important;
        color: ${theme.palette.primary.contrastText} !important;
      }
      .fc-button-active {
        background-color: ${theme.palette.primary.main} !important;
        border-color: ${theme.palette.primary.main} !important;
        color: ${theme.palette.primary.contrastText} !important;
      }
      .fc-button-primary:not(:disabled):active,
      .fc-button-primary:not(:disabled).fc-button-active {
        background-color: ${theme.palette.primary.dark} !important;
        border-color: ${theme.palette.primary.dark} !important;
        color: ${theme.palette.primary.contrastText} !important;
      }
      .fc-today-button {
        background-color: ${theme.palette.background.paper};
        border-color: ${theme.palette.primary.main} !important;
        color: ${theme.palette.primary.main} !important;
      }
      .fc-today-button:hover {
        background-color: ${theme.palette.primary.light};
        color: ${theme.palette.primary.contrastText} !important;
      }
      .fc-today-button:disabled {
        background-color: ${theme.palette.primary.main} !important;
        border-color: ${theme.palette.primary.main} !important;
        color: ${theme.palette.primary.contrastText} !important;
      }
      .fc-daygrid-day-top {
        color: ${theme.palette.text.secondary};
      }
      .fc-col-header-cell {
        background-color: ${theme.palette.primary.light};
        color: ${theme.palette.secondary.contrastText};
        font-weight: 600;
        border-color: ${theme.palette.divider};
        font-size: ${isMobile ? '0.75rem' : '0.875rem'};
        padding: ${isMobile ? '8px 4px' : '8px'};
      }
      .fc-daygrid-day {
        background-color: ${theme.palette.background.paper};
        border-color: ${theme.palette.divider};
      }
      .fc-daygrid-day:hover {
        background-color: #F2F4FF;
      }
      .fc-day-today {
        background-color: #EEF0FF;
      }
      .fc-event {
        background-color: ${theme.palette.primary.main};
        border-color: ${theme.palette.primary.dark};
        border-radius: 6px;
        padding: ${isMobile ? '3px 6px' : '2px 4px'};
        font-size: ${isMobile ? '0.75rem' : '0.875rem'};
        min-height: ${isMobile ? '24px' : '20px'};
      }
      .fc-event:hover {
        background-color: ${theme.palette.primary.dark};
      }
      .fc-event-title {
        font-weight: 500;
      }
      .fc-daygrid-event {
        color: ${theme.palette.primary.contrastText};
      }
      .fc-daygrid-day-number {
        color: ${theme.palette.text.primary};
        padding: ${isMobile ? '6px' : '8px'};
        font-size: ${isMobile ? '0.875rem' : '1rem'};
      }
      .fc-daygrid-day-top {
        flex-direction: row;
      }
      .fc-toolbar-title {
        font-size: ${isMobile ? theme.typography.h6.fontSize : theme.typography.h6.fontSize};
        font-weight: ${theme.typography.h6.fontWeight};
        color: ${theme.palette.text.primary};
        text-align: ${isMobile ? 'center' : 'left'};
        margin: ${isMobile ? '0.5em 0' : '0'};
      }
      .fc-scrollgrid {
        border-color: ${theme.palette.divider};
        border-radius: ${theme.shape.borderRadius}px;
        overflow: hidden;
      }
      .fc-scrollgrid-section > td {
        border-color: ${theme.palette.divider};
      }
      @media (max-width: 600px) {
        .fc-header-toolbar .fc-toolbar-chunk:first-child {
          order: 2;
        }
        .fc-header-toolbar .fc-toolbar-chunk:nth-child(2) {
          order: 1;
        }
        .fc-header-toolbar .fc-toolbar-chunk:last-child {
          order: 3;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [theme, isMobile]);

  const handleDateClick = (arg: DateClickArg) => {
    if (onDateClick) {
      onDateClick(arg.date);
    } else {
      console.log('Date clicked: ' + arg.date);
    }
  };

  const handleEventClick = (arg: EventClickArg) => {
    if (onEventClick) {
      onEventClick(arg);
    } else {
      console.log('Event clicked: ' + arg.event.title);
    }
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    if (onMonthChange) {
      onMonthChange(arg.start);
    }
  };

  function renderEventContent(eventContent: EventContentArg) {
    return (
      <>
        <b>{eventContent.timeText}</b>
        <i>{eventContent.event.title}</i>
      </>
    );
  }

  const INITIAL_EVENTS: EventInput[] = [
    {
      id: createEventId(),
      title: translateText('calendar.allDayEvent'),
      start: todayStr,
    },
    {
      id: createEventId(),
      title: translateText('calendar.timedEvent'),
      start: todayStr + 'T12:00:00',
    },
  ];

  function createEventId() {
    return String(eventGuid++);
  }

  return (
    <Box
      sx={{
        '& .fc': {
          backgroundColor: theme.palette.background.paper,
          borderRadius: theme.shape.borderRadius,
          padding: { xs: 1, sm: 2 },
        },
      }}
      data-testid="calendar-component"
    >
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        locale={i18n.language}
        headerToolbar={{
          left: isMobile ? 'prev,next' : 'prev,today,next',
          center: 'title',
          right: isMobile ? 'today' : 'dayGridDay,dayGridWeek,dayGridMonth',
        }}
        events={events}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        weekends={true}
        weekNumbers={!isMobile}
        eventContent={renderEventContent}
        initialEvents={INITIAL_EVENTS}
        datesSet={handleDatesSet}
        height={isMobile ? 'auto' : undefined}
        aspectRatio={isMobile ? 1.2 : 1.35}
      />
    </Box>
  );
};

export default CalendarComponent;
